import BoletoIllustration from '../../../../../assets/checkout/woman-table.svg';
import clsx from 'clsx';
import illustration from '../../../../../assets/checkout/woman.svg';
import ornaments from '../../../../../assets/checkout/ornaments.png';
import {
  $,
  JSX,
  PropFunction,
  component$,
  createContextId,
  useContext,
  useContextProvider,
  useSignal,
  useStore,
  useVisibleTask$
} from '@builder.io/qwik';
import { Button } from '../../../../../components/ui/Button';
import {
  CheckoutService,
  GuestOrderPaymentShow,
  OrderPaymentShow
} from '../../../../../@generated/checkout-data';
import { Icon } from '../../../../../components/ui/Icon';
import { currencyFormatter } from '../../../../../lib/currency.lib';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { formatDate } from '@kratos/utils';
import { useCheckoutSuccessData } from '../index@clean';
import { useCurrentUser } from '../../../../../hooks/use-shell-state.hook';

const PAYMENT_CONTEXT_ID = createContextId<{
  currentStep: number;
  payment?:
    | OrderPaymentShow['payment']
    | GuestOrderPaymentShow['payment']
    | null;
}>('pix-context');

const HERO_SLOT_MAPPER: Record<
  string,
  {
    mainSlot: JSX.Element;
    descriptionSlot: JSX.Element;
    steps: { label: string; index: number }[];
  }
> = {
  TicketPayment: {
    mainSlot: (
      <span>
        sucesso <b> e o boleto já foi emitido para pagamento.</b>
      </span>
    ),
    descriptionSlot: <></>,
    steps: [
      { label: 'Compra Efetuada', index: 0 },
      { label: 'Pagamento do Boleto', index: 1 },
      { label: 'Pagamento Recebido', index: 2 }
    ]
  },
  PixPayment: {
    mainSlot: <span>sucesso.</span>,
    descriptionSlot: (
      <p
        class="text-md text-center text-white font-semibold"
        style="text-shadow: 0 2px 2px rgba(0,0,0,.15);"
      >
        Escaneie o QR code ou copie o código abaixo para realizar o pagamento.
      </p>
    ),
    steps: [
      { label: 'Compra Efetuada', index: 0 },
      { label: 'Pagamento do Pix', index: 1 },
      { label: 'Pagamento Recebido', index: 2 }
    ]
  },
  CreditCardPayment: {
    mainSlot: <span>sucesso.</span>,
    descriptionSlot: <></>,
    steps: [
      { label: 'Compra Efetuada', index: 0 },
      { label: 'Pagamento Recebido', index: 1 }
    ]
  },
  NupayPayment: {
    mainSlot: <span>sucesso.</span>,
    descriptionSlot: <></>,
    steps: [
      { label: 'Compra Efetuada', index: 0 },
      { label: 'Pagamento Recebido', index: 1 }
    ]
  }
};

export const LegacySuccess = component$(() => {
  const {
    value: { payment, order }
  } = useCheckoutSuccessData();
  const paymentType = payment?.type || '';
  const heroSlot = HERO_SLOT_MAPPER[paymentType] || {};
  const isFreeOrder = order.amount === 0;
  const paymentContext = useStore({
    payment,
    currentStep: payment?.approvedAt ? heroSlot?.steps?.length - 1 || 1 : 1
  });

  useContextProvider(PAYMENT_CONTEXT_ID, paymentContext);

  return (
    <>
      <SuccessHero {...heroSlot} />
      {isFreeOrder ? <LegacyFreePurchase /> : PAYMENT_TYPE_MAPPER[paymentType]}
    </>
  );
});

const SuccessHero = component$(
  ({
    mainSlot,
    descriptionSlot,
    steps
  }: {
    mainSlot?: JSX.Element;
    descriptionSlot?: JSX.Element;
    steps: { label: string; index: number }[];
  }) => {
    const context = useContext(PAYMENT_CONTEXT_ID);
    const currentStrep = context?.currentStep || 1;
    const {
      value: { order }
    } = useCheckoutSuccessData();

    const user = useCurrentUser();
    const userName = user?.name?.split(' ')?.[0] || 'Aluno';
    const orderNumber = order.number;

    return (
      <>
        <div class="relative w-full h-[200px] flex items-center justify-center flex-col">
          <div class="absolute inset-0 before:absolute before:inset-0 before:bg-gradient-to-r before:from-[#7bd8b7] before:to-[#23be87] after:absolute after:inset-0 after:h-[300px] after:top-[-100px] after:bg-gradient-to-t after:from-black after:to-transparent after:opacity-20">
            <div
              class="absolute inset-0 bg-cover bg-bottom"
              style={{ backgroundImage: `url(${ornaments})` }}
            />
          </div>
          <div
            class="relative z-10"
            style="filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.15));"
          >
            <div class="w-full h-full flex flex-col items-center justify-center max-w-[450px]">
              <p
                class="text-2xl font-semibold text-center text-white mb-3"
                style="text-shadow: 0 2px 2px rgba(0,0,0,.15);"
              >
                Olá, {userName}!
              </p>
              <p
                class="text-md text-center text-white"
                style="text-shadow: 0 2px 2px rgba(0,0,0,.15);"
              >
                Seu pedido <span class="font-semibold">#{orderNumber}</span> foi
                efetuado com {mainSlot || 'sucesso'}
              </p>
              {descriptionSlot}
            </div>
          </div>
        </div>
        <div class="relative top-[-30px]">
          <StepIndicator currentStep={currentStrep} steps={steps} />
        </div>
      </>
    );
  }
);

const StepIndicator = component$(
  ({
    currentStep,
    steps
  }: {
    currentStep: number;
    onStepChange$?: PropFunction<(step: number) => void>;
    steps: { label: string; index: number }[];
  }) => (
    <div class="flex items-center justify-between w-full max-w-[698px] p-4 mx-auto">
      {steps?.map((step, index) => {
        const stepsLength = steps.length - 1;
        const isFinalStep = stepsLength === index && currentStep === index;
        const allStepsDone = currentStep === stepsLength;

        return (
          <div
            key={index}
            class={clsx(
              'flex items-center space-x-4 relative w-full justify-center min-h-[84px] h-[104px] sm:h-auto',
              {
                'after:absolute after:inset-y-0 after:right-0 after:w-[50%] after:top-[35%] after:h-[2px]':
                  index !== steps.length - 1,
                'before:absolute before:inset-y-0 before:left-0 before:w-[50%] before:top-[35%] before:h-[2px]':
                  index !== 0,
                'after:bg-gray-300 before:bg-emerald-500':
                  currentStep === index && !allStepsDone,
                'after:bg-gray-300 before:bg-gray-300':
                  currentStep < index && !allStepsDone,
                'before:bg-gray-300 after:bg-emerald-500':
                  currentStep > index && !allStepsDone,
                'before:bg-emerald-500 after:bg-emerald-500': allStepsDone
              }
            )}
          >
            <div class="flex flex-col items-center h-full">
              <div class="flex items-center justify-center rounded-full p-3 bg-gray-100 z-[3]">
                <div
                  class={clsx(
                    'flex items-center justify-center rounded-full border-2',
                    {
                      'w-7 h-7 bg-emerald-500 border-emerald-500 relative':
                        currentStep > index || isFinalStep,
                      'w-7 h-7 bg-gray-100 border-green-500 absolute z-[3]':
                        currentStep === index && !isFinalStep,
                      'w-7 h-7 bg-gray-100 border-gray-300 relative':
                        currentStep < index
                    }
                  )}
                >
                  {(currentStep > index || isFinalStep) && (
                    <svg
                      class="w-6 h-6 mt-1 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        clip-rule="evenodd"
                        d="M16.707 5.293a1 1 0 00-1.414-1.414L9 10.586 6.707 8.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7z"
                        fill-rule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                {currentStep === index && !isFinalStep && (
                  <div class="w-7 h-7 bg-green-200 rounded-full animate-ping" />
                )}
              </div>
              <span
                class={clsx('text-center', {
                  'text-zinc-700 mt-2 font-medium max-w-[77px] sm:max-w-full text-xs':
                    currentStep !== index || isFinalStep,
                  'text-zinc-700 mt-3 font-semibold max-w-[77px] sm:max-w-full text-sm':
                    currentStep === index && !isFinalStep
                })}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  )
);

const LegacyBoleto = component$(() => {
  const {
    value: { payment }
  } = useCheckoutSuccessData();
  const handlePrintBoleto = $(() => {
    if (payment?.externalUrl) {
      window.open(payment?.externalUrl, '_blank');
    }
  });

  return (
    <div class="flex flex-col lg:flex-row items-center justify-center bg-white shadow-md p-8 rounded-sm gap-10 mt-6 container mx-auto max-w-[1170px]">
      <img width="408" height="273" loading="lazy" src={BoletoIllustration} />
      <div>
        <p class="text-md font-bold text-zinc-800 mb-4">
          Parabéns pela sua aquisição de um novo plano!
        </p>
        <p class="text-sm text-zinc-700 mb-6 max-w-[390px]">
          Para começar a usar seus benefícios, efetue o pagamento do boleto.
          <br />
          <br />
          Volte a <span class="font-semibold">praticar questões</span> para
          absorver todo o conteúdo da melhor forma.
        </p>
        <div class="flex gap-4 justify-center">
          <Button variant="brand" onClick$={handlePrintBoleto}>
            Imprimir boleto
          </Button>
          <Button variant="default">Quero praticar agora</Button>
        </div>
      </div>
    </div>
  );
});

const LegacyPix = component$(() => {
  const context = useContext(PAYMENT_CONTEXT_ID);
  const {
    value: { payment, order, builderHtml, showBuilder }
  } = useCheckoutSuccessData();

  const timeoutId = useSignal<NodeJS.Timeout | null>();
  const isPooling = useSignal(false);
  const store = useStore({
    paymentCode: payment?.pixCode?.code,
    countdown: ''
  });

  const expiration = formatDate(payment?.expiresAt || '', true);

  const handlePolling$ = $(async () => {
    if (isPooling.value || !payment?.id) {
      return;
    }

    isPooling.value = true;

    const checkoutService = new CheckoutService({
      credentials: 'include'
    });

    const response = await checkoutService
      .getV2OrdersPayment({
        number: String(order.number),
        id: Number(payment?.id)
      })
      .catch(() => {
        isPooling.value = false;
      });

    if (!response) {
      return;
    }

    if (['expired', 'paid', 'approved'].includes(response.payment.status)) {
      context.payment = response.payment;
      isPooling.value = false;
      timeoutId.value && clearTimeout(timeoutId.value);

      if (['approved', 'paid'].includes(response.payment.status)) {
        context.currentStep = 2;
      }

      return;
    }

    isPooling.value = false;
    timeoutId.value && clearTimeout(timeoutId.value);
    timeoutId.value = null;
  });

  // eslint-disable-next-line qwik/no-use-visible-task
  useVisibleTask$(({ track }) => {
    track(() => payment?.expiresAt);
    track(() => timeoutId.value);

    if (!timeoutId.value) {
      timeoutId.value = setTimeout(() => handlePolling$(), 5000);
    }
  });

  const copyCode = $(() => {
    if (store.paymentCode) {
      navigator.clipboard.writeText(store.paymentCode);
      alert('Código copiado!');
    }
  });

  return (
    <>
      {['approved', 'paid'].includes(
        context.payment?.status || payment?.status || ''
      ) ? (
        <>
          {showBuilder ? (
            <div dangerouslySetInnerHTML={builderHtml} />
          ) : (
            <LegacyStartStudying />
          )}
        </>
      ) : (
        <>
          {['expired', 'rejected'].includes(
            context.payment?.status || payment?.status || ''
          ) ? (
            <div class="container">
              <div class="flex flex-col text-center items-center justify-center bg-gray-200 p-3 py-8 max-w-[600px">
                <p class="text-red-500 text-3xl pb-3">
                  <Icon icon={faExclamationCircle} />
                </p>
                <p class="text-lg font-semibold text-center pb-3">
                  O CÓDIGO EXPIROU
                </p>
                <p class="text-md text-center pb-4">
                  Para pagar, realize sua compra novamente
                </p>
                <Button
                  variant="secondary"
                  onClick$={() => {
                    window.location.href =
                      'https://www.qconcursos.com/planos-de-assinatura?qtm_content=Ir+para+a+p%C3%A1gina+de+planos&qtm_medium=pix-expired&qtm_source=qconcuros';
                  }}
                >
                  Ir para a página de planos
                </Button>
              </div>
            </div>
          ) : (
            <div class="container">
              <div class="relative flex flex-col items-center w-full border rounded-md bg-white max-w-[1072px] mx-auto">
                <div class="flex flex-col justify-center items-center w-full mb-6 gap-4 bg-gray-200 p-2.5">
                  <p class="text-md md:text-2xl font-semibold text-zinc-700 text-center">
                    Valor do Pix a pagar:{' '}
                    {currencyFormatter.format(
                      (payment?.chargedAmount || 0) / 100
                    )}
                  </p>
                </div>
                <div class="px-8 pb-8 w-full max-w-[720px]">
                  <div class="max-w-[578px] mx-auto">
                    <p class="text-sm md:text-md text-center font-semibold text-zinc-700 mb-4">
                      O seu Pix será reconhecido em até 3 minutos e, em seguida,
                      o seu acesso à plataforma será liberado. Por favor, não
                      feche nem atualize essa tela. Aguarde enquanto estamos
                      processando o seu pagamento.
                    </p>
                    <p class="text-xs text-center font-semibold text-zinc-700 md:mb-4">
                      Código válido até {expiration}
                    </p>
                    <div class="flex flex-col lg:flex-row items-center gap-2 justify-center">
                      <div class="flex-grow-0 flex-shrink-0">
                        <img
                          class="w-64 h-64"
                          width={224}
                          height={224}
                          alt="QR Code"
                          src={`data:image/png;base64,${payment?.pixCode?.base64Code}`}
                        />
                      </div>
                      <div class="flex flex-col items-start space-y-1">
                        <div class="flex items-center">
                          <div class="flex flex-col items-center">
                            <div class="flex items-center justify-center w-8 h-8 text-sm font-semibold text-emerald-500 border border-gray-400 rounded-full">
                              <span class="text-teal-500">1</span>
                            </div>
                            <svg
                              width="8"
                              height="34"
                              fill="none"
                              viewBox="0 0 8 34"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.64645 33.3536C3.84171 33.5488 4.15829 33.5488 4.35355 33.3536L7.53554 30.1716C7.7308 29.9763 7.7308 29.6597 7.53554 29.4645C7.34027 29.2692 7.02369 29.2692 6.82843 29.4645L4 32.2929L1.17157 29.4645C0.976312 29.2692 0.65973 29.2692 0.464467 29.4645C0.269205 29.6597 0.269205 29.9763 0.464467 30.1716L3.64645 33.3536ZM3.5 2.18557e-08L3.5 33L4.5 33L4.5 -2.18557e-08L3.5 2.18557e-08Z"
                                fill="#6D767E"
                              />
                            </svg>
                          </div>
                          <p class="ml-4 text-gray-600 text-xs self-start">
                            Abra o app do seu banco ou instituição financeira e
                            entre no ambiente <span class="font-bold">Pix</span>
                          </p>
                        </div>
                        <div class="flex items-center">
                          <div class="flex flex-col items-center">
                            <div class="flex items-center justify-center w-8 h-8 text-sm font-semibold text-emerald-500 border border-gray-400 rounded-full">
                              <span class="text-teal-500">2</span>
                            </div>
                            <svg
                              width="8"
                              height="34"
                              fill="none"
                              viewBox="0 0 8 34"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.64645 33.3536C3.84171 33.5488 4.15829 33.5488 4.35355 33.3536L7.53554 30.1716C7.7308 29.9763 7.7308 29.6597 7.53554 29.4645C7.34027 29.2692 7.02369 29.2692 6.82843 29.4645L4 32.2929L1.17157 29.4645C0.976312 29.2692 0.65973 29.2692 0.464467 29.4645C0.269205 29.6597 0.269205 29.9763 0.464467 30.1716L3.64645 33.3536ZM3.5 2.18557e-08L3.5 33L4.5 33L4.5 -2.18557e-08L3.5 2.18557e-08Z"
                                fill="#6D767E"
                              />
                            </svg>
                          </div>
                          <p class="ml-4 text-gray-600 text-xs self-start">
                            Escolha a opção{' '}
                            <span class="font-bold">pagar com QR code</span> e
                            escaneie ou copie o código
                          </p>
                        </div>
                        <div class="flex items-center">
                          <div class="flex flex-col items-center">
                            <div class="flex items-center justify-center w-8 h-8 text-sm font-semibold text-emerald-500 border border-gray-400 rounded-full">
                              <span class="text-teal-500">3</span>
                            </div>
                          </div>
                          <p class="ml-4 text-gray-600 text-xs self-start">
                            Confirme as informações e finalize a compra
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr class="w-full my-4 border-gray-300" />
                  <p class="my-6 text-lg font-semibold text-center text-gray-700">
                    Se preferir, você pode pagá-lo copiando e colando o código
                    abaixo:
                  </p>
                  <div class="flex flex-col justify-center items-center w-full gap-4">
                    <div class="flex flex-col justify-start items-start w-full h-12 gap-1.5">
                      <input
                        class="border border-gray-300 rounded-lg bg-gray-50 h-12 px-4 w-full"
                        style="box-shadow: 0px 1px 2px 0 rgba(16,24,40,0.05);"
                        readOnly={true}
                        type="text"
                        value={payment?.pixCode?.code}
                      />
                    </div>
                    <Button
                      class="w-full max-w-[200px] mt-4"
                      size="lg"
                      variant="secondary"
                      onClick$={copyCode}
                    >
                      Copiar código
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
});

export const LegacyStartStudying = component$(() => {
  const {
    value: { mentoringSuccess, order }
  } = useCheckoutSuccessData();

  const isMentoring = order.currentKind === 'mentoring' && mentoringSuccess;

  return (
    <div class="flex flex-col lg:flex-row items-center justify-center bg-white shadow-lg p-8 rounded-md gap-10 mt-10 max-w-[1024px] mx-auto">
      <img width="408" height="273" loading="lazy" src={illustration} />
      <div>
        <p class="text-lg font-bold text-gray-800 mb-2">
          {isMentoring
            ? 'Parabéns pela aquisição da mentoria!'
            : 'Obrigado por fazer parte do Qconcursos.com!'}
        </p>
        <p class="text-sm text-gray-600 mb-6">
          {isMentoring
            ? 'Clique no botão abaixo para cessar detalhes da sua mentoria!'
            : 'Não perca mais tempo e comece agora a resolver suas primeiras questões como assinante!'}
        </p>
        {isMentoring ? (
          <p class="text-sm text-gray-600 mb-6">
            Para mais detalhes sobre a Mentoria, visualize o e-mail que você
            recebeu
          </p>
        ) : (
          <></>
        )}
        <div class="w-full flex gap-3">
          <Button
            variant="brand"
            onClick$={() => {
              const url = isMentoring
                ? 'https://app.qconcursos.com/b/dashboard'
                : 'https://app.qconcursos.com/playground/filtro';

              window.location.href = url;
            }}
          >
            {isMentoring ? 'Acessar mentoria' : 'Comece a praticar'}
          </Button>
          {isMentoring && (
            <Button
              variant="secondary"
              onClick$={() => {
                const url = isMentoring
                  ? 'https://app.qconcursos.com/b/guia-de-estudos/busca/todos'
                  : 'https://app.qconcursos.com/playground/filtro';

                window.location.href = url;
              }}
            >
              Escolher um curso
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

const LegacyCreditCardPayment = component$(() => {
  const {
    value: { builderHtml, showBuilder }
  } = useCheckoutSuccessData();

  return (
    <>
      {showBuilder ? (
        <div dangerouslySetInnerHTML={builderHtml} />
      ) : (
        <LegacyStartStudying />
      )}
    </>
  );
});

const LegacyNupayPayment = component$(() => {
  const {
    value: { builderHtml, showBuilder }
  } = useCheckoutSuccessData();

  return (
    <>
      {showBuilder ? (
        <div dangerouslySetInnerHTML={builderHtml} />
      ) : (
        <LegacyStartStudying />
      )}
    </>
  );
});

const LegacyFreePurchase = component$(() => {
  const {
    value: { builderHtml, showBuilder }
  } = useCheckoutSuccessData();

  return (
    <>
      {showBuilder ? (
        <div dangerouslySetInnerHTML={builderHtml} />
      ) : (
        <LegacyStartStudying />
      )}
    </>
  );
});

const PAYMENT_TYPE_MAPPER: Record<string, JSX.Element> = {
  PixPayment: <LegacyPix />,
  TicketPayment: <LegacyBoleto />,
  CreditCardPayment: <LegacyCreditCardPayment />,
  NupayPayment: <LegacyNupayPayment />
};
